import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Modal, Button } from '../common';
import { useAuth } from '../../context/AuthContext';
import { createProject } from './ocrApi';

/**
 * 새 OCR 프로젝트 생성 모달 (FE-2).
 *
 * 필드: 이름(필수 ≤200) + 키워드 칩(Enter/콤마 추가, ≤100자, 중복 제거) + 번호체계 라디오.
 * 저장 성공 → onCreated(생성된 프로젝트 data) — 부모(ProjectListPanel)가 목록 갱신 + 상세 이동.
 */
function ProjectCreateModal({ isOpen, onClose, onCreated }) {
  const { getAccessToken } = useAuth();

  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [numberingMode, setNumberingMode] = useState(0); // 0 단일 연속 / 1 단원별 재시작
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setName('');
    setKeywords([]);
    setKeywordInput('');
    setNumberingMode(0);
    setSaving(false);
    setError(null);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const addKeyword = (raw) => {
    const kw = String(raw || '').trim();
    if (!kw || kw.length > 100) return;
    setKeywords((prev) => (prev.includes(kw) ? prev : [...prev, kw]));
  };

  // 붙여넣기 등으로 콤마가 섞여 들어오면 앞부분들을 즉시 칩으로 확정
  const handleKeywordChange = (e) => {
    const v = e.target.value;
    if (v.includes(',')) {
      const parts = v.split(',');
      const rest = parts.pop();
      parts.forEach(addKeyword);
      setKeywordInput(rest);
    } else {
      setKeywordInput(v);
    }
  };

  const handleKeywordKeyDown = (e) => {
    if (e.nativeEvent.isComposing) return; // 한글 IME 조합 중 Enter는 조합 확정이므로 무시
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword(keywordInput);
      setKeywordInput('');
    } else if (e.key === 'Backspace' && !keywordInput && keywords.length > 0) {
      setKeywords((prev) => prev.slice(0, -1));
    }
  };

  const removeKeyword = (kw) => setKeywords((prev) => prev.filter((k) => k !== kw));

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    if (trimmed.length > 200) {
      setError('프로젝트 이름은 200자 이하여야 합니다.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // 입력창에 남아 있는 미확정 키워드도 포함(칩 추가를 잊은 경우) — 콤마 구분·중복 제거 규칙 동일 적용
      const pending = keywordInput
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s && s.length <= 100);
      const finalKeywords = [...keywords];
      pending.forEach((kw) => {
        if (!finalKeywords.includes(kw)) finalKeywords.push(kw);
      });
      const data = await createProject(
        { name: trimmed, keywords: finalKeywords, numberingMode },
        getAccessToken,
      );
      reset();
      onCreated && onCreated(data);
    } catch (e) {
      setError(e.message || '프로젝트 생성에 실패했습니다.');
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="새 OCR 프로젝트"
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={saving}>
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={!name.trim() || saving} loading={saving}>
            생성
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* 이름 */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            프로젝트 이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder="예: 쎈 공통수학1"
            className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none"
          />
        </div>

        {/* 키워드 칩 */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">키워드</label>
          <div className="flex flex-wrap items-center gap-1.5 rounded-xl border px-3 py-2">
            {keywords.map((kw) => (
              <span
                key={kw}
                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
              >
                {kw}
                <button
                  type="button"
                  onClick={() => removeKeyword(kw)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label={`${kw} 삭제`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              type="text"
              value={keywordInput}
              onChange={handleKeywordChange}
              onKeyDown={handleKeywordKeyDown}
              maxLength={100}
              placeholder={keywords.length === 0 ? 'Enter 또는 콤마로 추가 (예: 쎈, 공통수학1)' : ''}
              className="min-w-[120px] flex-1 border-none py-0.5 text-sm focus:outline-none"
            />
          </div>
          {/* 키워드 = 승격 공통 라벨(BE 회신 Q6) — 생성 후엔 편집 UI가 없어 여기서 정확히 넣어야 한다 */}
          <p className="mt-1 text-xs text-gray-400">등록 시 이 프로젝트 전 문제의 공통 라벨이 됩니다 (문제 번호 &quot;N번&quot; 라벨은 자동 부여).</p>
        </div>

        {/* 번호체계 */}
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">문제 번호체계</label>
          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700">
              <input
                type="radio"
                name="numbering_mode"
                checked={numberingMode === 0}
                onChange={() => setNumberingMode(0)}
              />
              단일 연속
            </label>
            <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-700">
              <input
                type="radio"
                name="numbering_mode"
                checked={numberingMode === 1}
                onChange={() => setNumberingMode(1)}
              />
              단원별 재시작
            </label>
          </div>
        </div>

        {error && <div className="text-sm text-red-600">{error}</div>}
      </div>
    </Modal>
  );
}

export default ProjectCreateModal;
